import { motion, useReducedMotion } from 'framer-motion'
import type { Product } from '@/types'
import { HomeProductGrid } from '@/store/components/HomeProductGrid'
import { HomeSection } from './HomeSection'
import { TextLink } from '@/store/ui'
import { homeEase, homeFadeUp } from './motion'

export interface ProductShowcaseBlock {
  id: string
  title: string
  subtitle?: string
  href: string
  linkLabel?: string
  products: Product[]
  gridClassName?: string
}

interface HomeProductShowcaseProps {
  sections: ProductShowcaseBlock[]
}

export function HomeProductShowcase({ sections }: HomeProductShowcaseProps) {
  const reduceMotion = useReducedMotion()
  const visible = sections.filter((s) => s.products.length > 0)

  if (visible.length === 0) return null

  return (
    <div className="space-y-4 md:space-y-8">
      {visible.map((block, blockIndex) => (
        <HomeSection
          key={block.id}
          id={block.id}
          eyebrow={blockIndex === 0 ? 'The collection' : undefined}
          title={block.title}
          subtitle={block.subtitle}
          actionHref={block.href}
          actionLabel={block.linkLabel ?? 'View all'}
          size="compact"
          className={blockIndex % 2 === 1 ? 'bg-luxury-900/30' : undefined}
        >
          <motion.div
            initial={reduceMotion ? false : 'hidden'}
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            variants={homeFadeUp}
            transition={{ duration: 0.5, ease: homeEase }}
          >
            <HomeProductGrid
              products={block.products}
              className={block.gridClassName}
            />
          </motion.div>
        </HomeSection>
      ))}
    </div>
  )
}

interface HomeRecommendationsProps {
  products: Product[]
}

export function HomeRecommendations({ products }: HomeRecommendationsProps) {
  const reduceMotion = useReducedMotion()

  if (products.length === 0) return null

  return (
    <HomeSection
      id="recommendations"
      eyebrow="Curated for you"
      title="Recommended for you"
      subtitle="Hand-picked luxury attars based on trending favourites across the collection."
      align="center"
      headerClassName="max-w-2xl"
      className="relative overflow-hidden"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-gold-100/20 via-transparent to-transparent"
        aria-hidden
      />
      <motion.div
        initial={reduceMotion ? false : 'hidden'}
        whileInView="visible"
        viewport={{ once: true, margin: '-40px' }}
        variants={homeFadeUp}
        transition={{ duration: 0.5, ease: homeEase }}
        className="relative"
      >
        <HomeProductGrid products={products} />
        <div className="mt-8 text-center">
          <TextLink to="/shop" underline>
            Explore full collection
          </TextLink>
        </div>
      </motion.div>
    </HomeSection>
  )
}
